import { AppletElementStatePolicy } from '../applet-element-state-policy';
import { AppletEmptyState } from '../applet-empty-state';
import { AppletEmptyStateType, isValidAppletEmptyState } from '../applet-empty-state-type';
import { BannerContent } from '../banner-content';
import { getTranslations } from '../translations';
import { MwAppletLoader } from '../mw-applet-loader';
import { statusCodeToEmptyStateAdapter } from '../status-code-to-empty-state-adapter';
import { appletElementPartialSuccessBannerMessage } from '../translations/translation-keys';
import styles from './index.scss';

const HIDDEN_BANNER = 'applet-element-content-banner__hidden';
const SHOWN_BANNER = 'applet-element-content-banner__shown';

/**
 * A class to decorate a DOM node with methods to display common applet views.
 *
 * @param {Node} element The DOM node to treat as an AppletElement
 */
export class AppletElement {
    constructor(element) {
        if (!(element instanceof Node)) {
            throw new TypeError(`element must be a valid dom Node. Provided value: ${JSON.stringify(element)}`);
        }

        this.statePolicy = new AppletElementStatePolicy();

        element.innerHTML = `
        <div class="applet-element-container">
            <style>${styles}</style>
            <${BannerContent.tagName} class="${HIDDEN_BANNER}"></${BannerContent.tagName}>
            <div class="applet-element-content"></div>
        </div>
        `;

        this.__bannerContent = element.querySelector(BannerContent.tagName);
        this.__element = element.querySelector('.applet-element-content');
        this.showLoader();
    }

    /**
     * Show the given DOM node as the sole content of the element
     *
     * @param {Node} htmlElement The DOM node to be the sole child of the AppletElement
     * @returns {Promise<undefined>} - Resolves after the state has been applied
     */
    async setContent(htmlElement) {
        if (!(htmlElement instanceof Node)) {
            throw new TypeError(`htmlElement must be a valid dom Node. Provided value: ${JSON.stringify(htmlElement)}`);
        }

        this.__setChild({ child: htmlElement });

        await this.statePolicy.applySetContentPolicy();
    }

    /**
     * Show the given DOM node along with a message banner, containing the supplied text
     *
     * @param {Node} htmlElement The DOM node to be the sole child of the AppletElement
     * @param {string} message The message to display in the banner
     * @returns {Promise<undefined>} - Resolves after the state has been applied
     */
    async setContentAndBanner(htmlElement, message) {
        if (!(htmlElement instanceof Node)) {
            throw new TypeError(`htmlElement must be a valid dom Node. Provided value: ${JSON.stringify(htmlElement)}`);
        }

        if (typeof message !== 'string') {
            throw new TypeError(`message must be a valid string. Provided value: ${JSON.stringify(message)}`);
        }

        this.__setChild({ child: htmlElement, bannerMessage: message });

        await this.statePolicy.applySetContentAndBannerPolicy();
    }

    /**
     * Show the given DOM node along with a partial content banner
     *
     * @param {Node} htmlElement The DOM node to be the sole child of the AppletElement
     * @returns {Promise<undefined>} - Resolves after the state has been applied
     */
    async setPartialContent(htmlElement) {
        if (!(htmlElement instanceof Node)) {
            throw new TypeError(`htmlElement must be a valid dom Node. Provided value: ${JSON.stringify(htmlElement)}`);
        }

        const translations = await getTranslations();
        const message = translations.getTranslation(appletElementPartialSuccessBannerMessage);

        this.__setChild({ child: htmlElement, bannerMessage: message });

        await this.statePolicy.applySetPartialContentPolicy();
    }

    /**
     * Show the loading state
     *
     * @returns {Promise<undefined>} - Resolves after the state has been applied
     */
    async showLoader() {
        this.__setChild({ child: document.createElement(MwAppletLoader.tagName) });

        await this.statePolicy.applyShowLoaderPolicy();
    }

    /**
     * Show the empty state corresponding to a specific http status
     *
     * @param {number} statusCode - The error http status code from a backend service
     * @returns {Promise<undefined>} - Resolves after the state has been applied
     */
    async showEmptyStateByStatusCode(statusCode) {
        const emptyStateType = statusCodeToEmptyStateAdapter(statusCode);

        const emptyState = document.createElement(AppletEmptyState.tagName);
        emptyState.setAttribute('type', emptyStateType);
        this.__setChild({ child: emptyState });

        await this.statePolicy.applyShowEmptyStateByStatusCodePolicy();
    }

    /**
     * Show the given empty state
     *
     * @param {AppletEmptyStateType} emptyStateType The empty state to show in the AppletElement
     * @returns {Promise<undefined>} - Resolves after the state has been applied
     */
    async showEmptyState(emptyStateType) {
        if (!isValidAppletEmptyState(emptyStateType)) {
            throw new TypeError(`Provided emptyStateType is not a valid AppletEmptyStateType. Provided value: ${emptyStateType}`);
        }

        const emptyState = document.createElement(AppletEmptyState.tagName);
        emptyState.setAttribute('type', emptyStateType);
        this.__setChild({ child: emptyState });

        await this.statePolicy.applyShowEmptyStatePolicy();
    }

    __setChild({ child, bannerMessage = undefined }) {
        if (this.__child) {
            this.__element.replaceChild(child, this.__child);
        }
        else {
            this.__element.appendChild(child);
        }

        if (bannerMessage) {
            const bannerNode = document.createElement('div');
            bannerNode.textContent = bannerMessage;
            bannerNode.setAttribute('title', bannerMessage);

            if (this.__bannerMessage) {
                this.__bannerContent.replaceChild(bannerNode, this.__bannerMessage);
            } else {
                this.__bannerContent.appendChild(bannerNode);
            }
            this.__bannerMessage = bannerNode;
            this.__bannerContent.className = SHOWN_BANNER;
        } else {
            this.__bannerContent.className = HIDDEN_BANNER;
        }

        this.__child = child;
    }
}
